import React, { useState, useEffect } from "react";
import {
    Box, Button, Container, Grid2, Pagination, Alert,
    Avatar,
    Stack,
    Chip,
    Paper,
    Divider,
    Typography,
} from "@mui/material";
import { Link, useLocation, useNavigate, NavLink } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useDispatch, useSelector } from "react-redux";
import Header from "../Layout/Header/Header";
import FooterCopy from "../Footer/FooterCopy";
import SkeletonTable from "../Skeleton/TableSkeleton";
import { getAllTradePairList } from "../../Actions/TradePairs/TradePairAction";
import { getTopMarketTradePairList } from "../../Actions/TradePairs/TopMarketListAction";
import { getUserDetail } from "../../Actions/UserDetailAction";
import socket from "../../utils/Socket";
import useBinanceAllTickers from "../../socket/BinanceMarketSocket";
import { formatNumberAbbreviation, handleImageError } from "../../Helper/helper";
import './Market.css'
import Image1 from '../../Images/gain-1.svg'
import Images2 from '../../Images/eth.svg'
import NOT from "../../Images/nodata.webp"
import "./Market.css";
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import Modal from '@mui/material/Modal';
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '300px', md: '450px' },
    bgcolor: 'var(--color-BasicBg)',
    borderRadius: "10px",
    boxShadow: 24,
    p: 3,
};


const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#3665ff",
        color: "#fff",
        fontSize: 14,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        border: "none",
    },
}));

const StyledTableRow = styled(TableRow)(() => ({
    "&:nth-of-type(even)": {
        backgroundColor: "#3665ff0d",
    },
}));


const Market = () => {

    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector(state => state.auth);
    const { userDetailData } = useSelector(state => state.user);
    const { tradePairData, tradePairLoading, tradePairStatus } = useSelector(state => state.tradePair);
    const { topMarketListData, topMarketListLoading, topMarketListStatus } = useSelector(state => state.topMarketList);

    const queryParams = new URLSearchParams(location.search);
    const pageFromUrl = Number(queryParams.get("page")) || 1;

    //modal1
    const [reviewDetailsOpen, setReviewDetailsOpen] = React.useState(false);
    const handleReviewDetailsOpen = () => setReviewDetailsOpen(true);
    const handleReviewDetailsClose = () => setReviewDetailsOpen(false);

    const [currentPage, setCurrentPage] = useState(pageFromUrl);
    const [totalPages, setTotalPages] = useState(1);

    const [tradePairList, setTradePairList] = useState([]);
    const [topGainers, setTopGainers] = useState([]);
    const [topLosers, setTopLosers] = useState([]);
    const [top24hVolume, setTop24hVolume] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const { tickers } = useBinanceAllTickers();

    useEffect(() => {
        dispatch(getAllTradePairList({ page: currentPage, limit: rowsPerPage }));
    }, [dispatch, currentPage]);

    useEffect(() => {
        dispatch(getTopMarketTradePairList());
    }, [dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(getUserDetail());
        }
    }, [dispatch, isAuthenticated]);


    useEffect(() => {

        if (!socket.connected) {
            socket.connect();
        }
        return () => {
            socket.off();
        };

    }, []);

    useEffect(() => {

        if (userDetailData?._id) {

            socket.emit("register", {
                ui: btoa(userDetailData._id),
                role: "user",
            });

        }

    }, [userDetailData]);



    useEffect(() => {

        if (tradePairStatus && tradePairData) {
            setTradePairList(tradePairData?.data || []);
            setTotalPages(tradePairData?.totalPages || 1);
        }

    }, [tradePairStatus, tradePairData]);



    useEffect(() => {
        if (topMarketListStatus && topMarketListData?.result) {
            setTopGainers(topMarketListData.result.topGainers || []);
            setTopLosers(topMarketListData.result.topLosers || []);
            setTop24hVolume(topMarketListData.result.top24hVolumes || []);
        }
    }, [topMarketListStatus, topMarketListData]);



    useEffect(() => {
        setCurrentPage(pageFromUrl);
    }, [location.search]);


    const handlePageChange = (_, value) => {

        setCurrentPage(value);

        if (value === 1) {
            navigate("");
        } else {
            navigate(`?page=${value}`);
        }

    };


    useEffect(() => {

        if (!tickers?.length) return;
        const priceMap = {};

        tickers.forEach(t => {
            priceMap[t.symbol.toUpperCase()] = {
                lastPrice: Number(t.lastPrice),
                change24h: Number(t.change24h),
                change1h: Number(t.change1h || 0),
                volume24h: Number(t.volume24h),
            };
        });

        const updatePrice = (symbol) => {
            if (!symbol) return null;
            return (
                priceMap[symbol.toUpperCase()] ||
                priceMap[`${symbol.toUpperCase()}USDT`]
            );
        };

        /* trending */
        setTopLosers(prev =>

            prev.map(item => {

                const update = updatePrice(item.source);
                if (!update) return item;
                return {
                    ...item,
                    usd_price: update.lastPrice,
                    percent_change_1h: update.change1h,
                    percent_change_24h: update.change24h,
                };
            })
        );

        /* gainers */
        setTopGainers(prev =>

            prev.map(item => {

                const update = updatePrice(item.source);
                if (!update) return item;
                return {
                    ...item,
                    usd_price: update.lastPrice,
                    percent_change_1h: update.change1h,
                    percent_change_24h: update.change24h,
                };
            })
        );

        /* volume */
        setTop24hVolume(prev =>

            prev.map(item => {

                const update = updatePrice(item.source);
                if (!update) return item;
                return {
                    ...item,
                    usd_price: update.lastPrice,
                    percent_change_24h: update.change24h,
                    volume_change_24h: update.volume24h,
                };
            })
        );

        /* trade pair */
        setTradePairList(prev =>

            prev.map(item => {

                const update = updatePrice(item.symbol);
                if (!update) return item;
                return {
                    ...item,
                    lastPrice: update.lastPrice,
                    change24h: update.change24h,
                    change1h: update.change1h,
                    baseVolume24h: update.volume24h,
                };

            })
        );

    }, [tickers]);



    return (

        <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", }}>
            <Header />

            <Container maxWidth="xl" sx={{ paddingTop: { xs: '70px', md: '100px' }, flexGrow: 1 }}>

                <div className="display-2 mb-20" ><h2 className="gold-color">Markets Overview</h2></div>
<Grid2 container spacing={2} className='mt-20'>

    {/* Top Gainers */}
    <Grid2 item size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 4 }}>
        {topMarketListLoading ? <SkeletonTable row={3} column={3} height={10} /> :
            <>
                <div className="market-coin-main">
                    <NavLink to="/trade" className='display-2'>
                        <div className="t-Caption1 text-PrimaryText">Top Gainers</div>
                    </NavLink>
                    <div className="mt-10">
                        {topGainers?.length > 0 ? topGainers.slice(0, 3).map((coin, index) => (
                            <Link
                                key={index}
                                to={`/trade?symbol=${coin.source}USDT`}
                                className="bn-balink"
                                role="link"
                            >
                                <div className="display-1">
                                    <img
                                        src={process.env.REACT_APP_BACKEND_URL + coin?.image}
                                        alt={coin.name}
                                        style={{ width: 25 }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = Image1;
                                        }}
                                    />
                                    <div>{coin.name}</div>
                                </div>
                                <div className="t-Body3">
                                    {Number(coin?.usd_price || 0).toFixed(6)}
                                </div>
                                <div className={`t-Subtitle4 ${parseFloat(coin?.percent_change_24h || 0) >= 0
                                    ? "text-green"
                                    : "text-red"
                                    }`}>
                                    {formatNumberAbbreviation(parseFloat(coin.percent_change_24h || 0))}%
                                </div>
                            </Link>
                        )) :
                            <div className="display-1"><div><label>No data found</label></div></div>
                        }
                    </div>
                </div>
            </>
        }
    </Grid2>

    {/* Top Losers */}
    <Grid2 item size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 4 }}>
        {topMarketListLoading ?
            <SkeletonTable row={3} column={3} height={10} />
            :
            <>
                <div className="market-coin-main">
                    <NavLink to="/trade" className='display-2'>
                        <div className="t-Caption1 text-PrimaryText">Top Losers</div>
                    </NavLink>
                    <div className="mt-10">
                        {topLosers?.length > 0 ?
                            topLosers?.slice(0, 3).map((coin, index) => (
                                <Link
                                    key={index}
                                    to={`/trade?symbol=${coin.source}USDT`}
                                    className="bn-balink"
                                    role="link"
                                >
                                    <div className="display-1">
                                        <img
                                            src={process.env.REACT_APP_BACKEND_URL + coin?.image}
                                            alt={coin.name}
                                            style={{ width: 25 }}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = Image1;
                                            }}
                                        />
                                        <div>{coin.name}</div>
                                    </div>
                                    <div className="t-Body3">
                                        {parseFloat(coin?.usd_price || 0).toFixed(2)}
                                    </div>
                                    <div className={`t-Subtitle4 ${parseFloat(coin?.percent_change_24h || 0) >= 0
                                        ? "text-green"
                                        : "text-red"
                                        }`}>
                                        {formatNumberAbbreviation(parseFloat(coin.percent_change_24h || 0))}%
                                    </div>
                                </Link>
                            ))
                            :
                            <div className="display-1">
                                <div><label>No data found</label></div>
                            </div>
                        }
                    </div>
                </div>
            </>
        }
    </Grid2>

    {/* Top Volume Coin */}
    <Grid2 item size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 4 }}>
        {topMarketListLoading ?
            <SkeletonTable row={3} column={3} height={10} />
            :
            <>
                <div className="market-coin-main">
                    <NavLink to="/trade" className='display-2'>
                        <div className="t-Caption1 text-PrimaryText">Top Volume Coin (24h)</div>
                    </NavLink>
                    <div className="mt-10">
                        {top24hVolume?.length > 0 ? top24hVolume.slice(0, 3).map((coin, index) => (
                            <Link
                                key={index}
                                to={`/trade?symbol=${coin.source}USDT`}
                                className="bn-balink"
                                role="link"
                            >
                                <div className="display-1">
                                    <img
                                        src={process.env.REACT_APP_BACKEND_URL + coin?.image}
                                        alt={coin.name}
                                        style={{ width: 25 }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = Image1;
                                        }}
                                    />
                                    <div>{coin.name}</div>
                                </div>
                                <div className="t-Body3">
                                    {parseFloat(coin?.usd_price || 0).toFixed(coin?.priceDecimal || 2)}
                                </div>
                                <div className="t-Subtitle4 text-green">
                                    {formatNumberAbbreviation(parseFloat(coin.volume_change_24h || 0))}
                                </div>
                            </Link>
                        )) :
                            <div className="display-1"><div><label>No data found</label></div></div>
                        }
                    </div>
                </div>
            </>
        }
    </Grid2>

</Grid2>

                <div className="mt-30">

                    <TableContainer sx={{ maxHeight: 600, scrollbarWidth: 'none', borderRadius: '12px 12px 12px 12px' }} className="mt-30">
                        {/* {usdttable} */}
                        {tradePairLoading ?
                            <SkeletonTable row={rowsPerPage} column={10} />
                            :
                            <>
                                <Table sx={{ minWidth: 400, background: "var(--color-BasicBg)" }} stickyHeader aria-label="sticky table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>No</TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex' }}>
                                                    <div className="last-bn-svg">Name</div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex' }}>
                                                    <div className="last-bn-svg">Price</div>
                                                </div>
                                            </TableCell>
                                            <TableCell align="right">
                                                <div style={{ display: 'flex' }}>
                                                    <div className="last-bn-svg">24h Change</div>
                                                </div>
                                            </TableCell>
                                            <TableCell align="right">
                                                <div style={{ display: 'flex' }}>
                                                    <div className="last-bn-svg">24h Volume</div>
                                                </div>
                                            </TableCell>
                                            <TableCell align="right">
                                                <div style={{ display: 'flex' }}>
                                                    <div className="last-bn-svg">Action</div>
                                                </div>
                                            </TableCell>

                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {tradePairList?.length > 0 ?
                                            tradePairList.map((row, index) => (
                                                <StyledTableRow key={index}>
                                                    <StyledTableCell component="td" scope="row" sx={{ width: "10px" }}>{(currentPage - 1) * rowsPerPage + index + 1}</StyledTableCell>
                                                    <StyledTableCell component="td" scope="row">
                                                        <div className="display-1 cursor" onClick={() => navigate(`/trade?symbol=${row.symbol}`)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <img src={row?.baseImage} alt={`${row.baseImage} logo`} style={{ width: 25 }} onError={handleImageError} />
                                                            <div className="display-1">
                                                                <h4 style={{ fontSize: '16px', margin: 0 }}>{row?.coinOne}</h4>
                                                                <p style={{ margin: 0, color: 'var(--color-textThird)' }}>{row?.symbol}</p>
                                                            </div>
                                                        </div>
                                                    </StyledTableCell>
                                                    <StyledTableCell>{parseFloat(row?.lastPrice || 0)?.toFixed(row?.priceDecimal || 2)}</StyledTableCell>
                                                    <StyledTableCell>
                                                        {parseFloat(row?.change24h || 0) > 0 || parseFloat(row?.change24h || 0) === 0 ?
                                                            <h4 className="green">{parseFloat(row?.change24h || 0).toFixed(2)}%</h4>
                                                            :
                                                            <h4 className="red">{parseFloat(row?.change24h || 0).toFixed(2)}%</h4>
                                                        }
                                                    </StyledTableCell>
                                                    <StyledTableCell>{formatNumberAbbreviation(parseFloat(row?.baseVolume24h))}</StyledTableCell>
                                                    <StyledTableCell>
                                                        <div className="blu-btn">
                                                            <Button className="cursor" onClick={() => navigate(`/investment?symbol=${row.coinOne}`)}>Invest</Button>
                                                            {/* <Button onClick={handleReviewDetailsOpen}>details</Button> */}
                                                        </div>
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                            ))
                                            :
                                            <StyledTableRow style={{ height: 100 }}>
                                                <StyledTableCell colSpan={8} style={{ textAlign: "center" }} >
                                                    <div className="Not-found mt-40">
                                                        <img src={NOT} alt="Not" style={{ width: "100px" }} />
                                                    </div>
                                                    <label style={{ fontSize: "15px" }}>
                                                        No data found!
                                                    </label>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        }
                                    </TableBody>
                                </Table>
                            </>
                        }
                    </TableContainer>

                    {/* {TablePaginations} */}

                    {tradePairList?.length > 0 && totalPages > 1 && (
                        <div className="Pagination-main">
                            <Pagination
                                page={currentPage}
                                count={totalPages}
                                onChange={handlePageChange}
                            />
                        </div>
                    )}

                </div>
            </Container>
            <FooterCopy />

            <Modal
                open={reviewDetailsOpen}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style} className="res-modal">
                    <div className="display-2">
                        <h2 className="gold-color" style={{ fontWeight: 900 }}>Review details</h2>
                        <CancelRoundedIcon onClick={() => { handleReviewDetailsClose() }} sx={{ color: "var(--color-TextLink)", fontSize: "30px", cursor: "pointer" }} />
                    </div>

                    <div className="input-common mt-10">
                        <Paper
                            elevation={0}
                            sx={{
                                textAlign: "center",
                                p: 1,
                                background: "transparent",
                            }}
                        >
                            {/* Illustration Icon */}
                            <Box
                                sx={{
                                    width: 90,
                                    height: 90,
                                    mx: "auto",
                                    mb: 2,
                                    borderRadius: "50%",
                                    background: "#F3F4F6",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <VerifiedUserOutlinedIcon
                                    sx={{ fontSize: 50, color: "#FF9800" }}
                                />
                            </Box>

                            {/* Title */}
                            <Typography
                                variant="h5"
                                fontWeight={700}
                                gutterBottom
                            >
                                Get verified
                            </Typography>

                            {/* Description */}
                            <Typography
                                variant="body1"
                                sx={{ mb: 3, lineHeight: 1.7 }}
                            >
                                To ensure your security and that of your counterparty,
                                please complete KYC Verification. Your information is
                                only used for identity verification.
                            </Typography>
                        </Paper>
                    </div>


                    <div className="blu-btn text-center mt-20">
                        <Button fullWidth>  Start Verification</Button>
                    </div>
                </Box>
            </Modal>
        </Box>
    )
}

export default Market